import {
  DashboardCardNameEnum,
  INamedFilterSchemaObject,
  IReportCriteriaFiltersSchemaValues,
  ReportPeriodGroupingEnum,
  RydeUiUnit,
} from "@ryde/shared";

/**
 * Entity key for the Dynamic Dashboard page. Used to scope the shared header
 * pieces (calendar / filters / views) via Recoil atomFamilies.
 */
export const DASHBOARD_ENTITY: RydeUiUnit = "dashboard";

/**
 * Max cards on a dashboard — fills the 4×3 grid. Card creation is blocked and
 * the "New" button disabled at this cap (the layout lives in free-form jsonb,
 * so the guard is client-side).
 */
export const DASHBOARD_MAX_CARDS = 12;

export type TDashboardCardSize = "narrow" | "wide";
export type TDashboardCardSource = "report" | "system";

/** Dashboard-owned chart types (the Reports engine is only used for data, not rendering). */
export enum DashboardChartType {
  VerticalBar = "verticalBar",
  HorizontalBar = "horizontalBar",
  Pie = "pie",
  Line = "line",
  Clustered = "clustered",
  Stacked = "stacked",
  Stat = "stat",
  /** Renders one of the real existing dashboard cards (by `legacyCardName`) with its live data. */
  Legacy = "legacy",
}

/** One data series for a multi-series (clustered/stacked) chart. */
export interface IDashboardSeries {
  label: string;
  values: number[];
}

/** Data for clustered/stacked charts: one value per (series, category). */
export interface IMultiSeriesContent {
  categories: string[];
  series: IDashboardSeries[];
}

export type TMetricUnit = "currency" | "percent";

/**
 * Data for a single-number "stat" card. No comparison figure: the shared numeric
 * template's subheader is written for a named card's "vs last <weekday>", which a
 * report metric has no equivalent for, so the card renders the value alone.
 */
export interface IDashboardStatData {
  value: number;
  unit?: TMetricUnit;
}

/**
 * A single dashboard chart card — either a `Legacy` card (renders a real
 * existing dashboard card by `legacyCardName` with its live data) or a custom
 * Reports-engine-backed chart (reportId / dimensionKey / metricKey).
 */
export interface IDashboardCard {
  id: string;
  title: string;
  subtitle: string;
  chartType: DashboardChartType;
  size: TDashboardCardSize;
  /** Optional target route (company-prefixed at click time) for the card's "open" button. */
  linkTo?: string;
  linkFilters?: INamedFilterSchemaObject;
  /**
   * Reports-engine binding for a custom card (absent for Legacy default cards).
   * Data is fetched via `ReportsApi.fetchReportData` — which enforces all
   * report/field/row-level permissions server-side — and reduced with the
   * reports engine's own aggregate utils.
   */
  reportId?: number;
  /** Chart dimension: the report column (uiTableColumn) / grouping key to break down by. */
  dimensionKey?: string;
  /** Second dimension for clustered/stacked charts — splits each category into series. */
  seriesKey?: string;
  /** Metric: the report summary column (uiTableColumn) to aggregate. */
  metricKey?: string;
  /** Flex grouping params sent so the report returns rows at the dimension's granularity. */
  groupingParams?: string[];
  /** Period grouping, when the chosen dimension is period-based. */
  periodGrouping?: ReportPeriodGroupingEnum;
  cardFilters?: IReportCriteriaFiltersSchemaValues;
  singleValueFilterKeys?: string[];
  /** Axis labels resolved at edit time (fallback to title / subtitle). */
  dimensionLabel?: string;
  metricLabel?: string;
  /** For `chartType === Legacy`: which real dashboard card to render (with its live data). */
  legacyCardName?: DashboardCardNameEnum;
}
